import {setTitle} from '@github-ui/document-metadata'
import {CurrentUserProvider} from '@github-ui/agent-sessions/contexts/CurrentUserContext'
import {ConsecutiveCAPI401sProvider} from '@github-ui/copilot-shared-ui/capi/ConsecutiveCAPI401sContext'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useCurrentQueryRoute} from '@github-ui/react-core/future/use-current-query-route'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {AppPayloadContext} from '@github-ui/react-core/use-app-payload'
import {useParams} from '@github-ui/react-router'
import {useEffect} from 'react'

import type {CopilotAgentsSpacesPayload} from '../../types/copilot-agents-spaces-payload'
import {AgentsSidebarLayout} from '../AgentsSidebarLayout'
import type {ChatPayload} from '../copilot-chat-payload-route-query'
import {CopilotChatProviders} from '../CopilotChatProviders'
import {AgentsSpaceDetailView} from './AgentsSpaceDetailView'
import {AgentsSpacesPageContent} from './AgentsSpacesPageContent'

export function CopilotAgentsSpaceDetailPage() {
  const currentRoute = useCurrentQueryRoute()
  const {data: payload} = useRouteQuery(currentRoute, 'mainQuery')
  const {data: chatPayload} = useRouteQuery(currentRoute, 'copilotChatPayload')
  const {owner, number} = useParams<'owner' | 'number'>()

  if (!payload || !chatPayload || !owner || !number) {
    return null
  }

  return (
    <CopilotAgentsSpaceDetailPageContent
      payload={payload as CopilotAgentsSpacesPayload}
      chatPayload={chatPayload}
      owner={owner}
      number={number}
    />
  )
}

export function CopilotAgentsSpaceDetailPageContent({
  payload,
  chatPayload,
  owner,
  number,
}: {
  payload: CopilotAgentsSpacesPayload
  chatPayload: ChatPayload
  owner: string
  number: string
}) {
  useEffect(() => {
    setTitle('Space · Agents · GitHub Copilot')
  }, [])

  return (
    <AppPayloadContext value={payload}>
      <CurrentUserProvider currentUser={payload.currentUser} currentUserIsSessionOwner>
        <ConsecutiveCAPI401sProvider>
          <ErrorBoundary boundaryName="CopilotAgentsSpaceDetailPageContent">
            <AgentsSidebarLayout
              agentsEnabled={!!payload.agentsEnabled}
              showSpacesNav={payload.spacesEnabled}
              showCustomizeNav={payload.customizeEnabled}
              currentUserLogin={payload.currentUser?.login}
              currentUserId={payload.currentUser?.id}
              enterpriseSandboxEnabled={payload.enterpriseSandboxEnabled !== false}
              taskGroups={payload.taskGroups}
              legacyChatPayload={chatPayload}
              contentKey="space-detail"
              sidebarProps={{isSpacesActive: true}}
            >
              <AgentsSpacesPageContent width="full" padding="none">
                {payload.spacesEnabled ? (
                  <CopilotChatProviders chatPayload={chatPayload}>
                    <AgentsSpaceDetailView owner={owner} number={Number(number)} />
                  </CopilotChatProviders>
                ) : (
                  <p>Spaces are not enabled for your account.</p>
                )}
              </AgentsSpacesPageContent>
            </AgentsSidebarLayout>
          </ErrorBoundary>
        </ConsecutiveCAPI401sProvider>
      </CurrentUserProvider>
    </AppPayloadContext>
  )
}
